'use client';

import { Button, Chip } from '@common/components';
import { SvgTabMoreFold } from '@common/icon';
import { cn } from '@core/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { useState } from 'react';

import { DONATION_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { gtmDatasets } from '@/utils/analytics/gtm';

import type { CategoryOption } from '../types';

interface CategoryFilterProps {
  categoryId?: number;
  options: CategoryOption[];
  className?: string;
}

export const CategoryFilter = ({
  categoryId,
  options,
  className,
}: CategoryFilterProps) => {
  const router = useCustomRouter();
  const [isFold, setIsFold] = useState(true);
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    dragFree: true,
    containScroll: 'trimSnaps',
  });

  const handleClick = (giftCategoryId?: number) => {
    const currentSearchParams = new URLSearchParams(window.location.search);
    if (giftCategoryId) {
      currentSearchParams.set('categoryId', giftCategoryId.toString());
    } else {
      currentSearchParams.delete('categoryId');
    }

    router.replace(
      DONATION_ROUTES.GIFTS.withSearchParams({
        searchParams: Object.fromEntries(currentSearchParams),
      }),
    );
  };

  return (
    <section
      className={cn(
        'relative z-[1] flex bg-Gray-white px-20 py-12',
        {
          '-mb-58 pb-20 border-b border-Gray-300 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.04),0px_8px_10px_0px_rgba(0,0,0,0.05)]':
            !isFold,
        },
        className,
      )}
    >
      {isFold ? (
        <div className="mx-0 overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {options.map(({ value, label }, index) => (
              <div key={index} className="w-fit flex-[0_0_auto]">
                <Chip
                  theme="primary"
                  className="h-38"
                  checked={value === 0 ? !categoryId : categoryId === value}
                  onClick={() => handleClick(value)}
                  {...gtmDatasets.clickTab({
                    tab_name: label,
                  })}
                >
                  {label}
                </Chip>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <ul className="flex flex-wrap items-center gap-6 gap-y-12 pr-62">
          {options.map(({ value, label }, index) => (
            <li key={index}>
              <Chip
                theme="primary"
                className="h-38"
                checked={value === 0 ? !categoryId : categoryId === value}
                onClick={() => handleClick(value)}
                {...gtmDatasets.clickTab({
                  tab_name: label,
                })}
              >
                {label}
              </Chip>
            </li>
          ))}
        </ul>
      )}

      <div className="absolute right-0 top-0 z-10 size-62 bg-[linear-gradient(90deg,rgba(255,255,255,0.00)_0%,#FFF_19.5%)] flex-center">
        <Button
          theme="sub-text"
          className="size-fit p-0"
          onClick={() => setIsFold(!isFold)}
        >
          <SvgTabMoreFold
            originalFill
            className={cn(
              'size-24 duration-300 [filter:drop-shadow(0px_4px_5px_rgba(0,_0,_0,_0.03))_drop-shadow(0px_0px_3px_rgba(0,_0,_0,_0.03))]',
              {
                '-rotate-180 duration-300': !isFold,
              },
            )}
          />
        </Button>
      </div>
    </section>
  );
};
